import { createApi } from '@reduxjs/toolkit/query/react'
import { BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { EnabledWorkflow, WorkflowSetting } from '../../models/Workflow'

const _BASE_URL = import.meta.env.REACT_APP_WORKFLOWS_SERVICE_API_BASE_URL!

export interface EnabledWorkflowCreatePayload {
  userId?: string
  workflowId: string
  context: { [key: string]: unknown }
}

export interface EnableWorkflowRequest extends BaseRequest {
  payload: EnabledWorkflowCreatePayload
}

export interface EnabledWorkflowUpdatePayload {
  context: { [key: string]: unknown }
}

export interface EnabledWorkflowUpdateRequest extends BaseRequest {
  enabledWorkflowId: string
  payload: EnabledWorkflowUpdatePayload
}

export interface DisableWorkflowRequest extends BaseRequest {
  enabledWorkflowId: string
}

export const enabledWorkflowApi = createApi({
  reducerPath: 'enabledWorkflowApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['EnabledWorkflows'],
  endpoints: (builder) => ({
    getAllEnabledWorkflows: builder.query<BaseResponse<EnabledWorkflow[]>, BaseRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<EnabledWorkflow>(
          {
            url: `/accounts/${request.accountId}/enabled-workflows`,
          },
          fetchWithBQ,
        )
      },
      providesTags: ['EnabledWorkflows'],
    }),
    getWorkflowSettings: builder.query<BaseResponse<WorkflowSetting[]>, BaseRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/workflow-settings`,
        method: 'GET',
      }),
      providesTags: ['EnabledWorkflows'],
    }),
    enableWorkflow: builder.mutation<EnabledWorkflow, EnableWorkflowRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/enabled-workflows/`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<EnabledWorkflow>) => {
        return response.data
      },
      invalidatesTags: ['EnabledWorkflows'],
    }),
    updateEnabledWorkflow: builder.mutation<EnabledWorkflow, EnabledWorkflowUpdateRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/enabled-workflows/${request.enabledWorkflowId}`,
        method: 'PATCH',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<EnabledWorkflow>) => {
        return response.data
      },
      invalidatesTags: ['EnabledWorkflows'],
    }),
    disableWorkflow: builder.mutation<void, DisableWorkflowRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/enabled-workflows/${request.enabledWorkflowId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EnabledWorkflows'],
    }),
  }),
})

export const {
  useGetAllEnabledWorkflowsQuery,
  useGetWorkflowSettingsQuery,
  useEnableWorkflowMutation,
  useDisableWorkflowMutation,
  useUpdateEnabledWorkflowMutation,
} = enabledWorkflowApi
